import { NavLink } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, Sparkles } from 'lucide-react';
import { NAV } from './nav.js';
import { cn } from '../lib/utils.js';
import { Tooltip } from '../components/ui/tooltip.js';

interface Props {
  collapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  /** Mobile drawer shows full labels and no collapse button. */
  mobile?: boolean;
}

export function Sidebar({ collapsed, onToggle, onNavigate, mobile }: Props) {
  const narrow = collapsed && !mobile;
  return (
    <aside aria-label="Main navigation"
      className={cn('flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200',
        narrow ? 'w-[68px]' : 'w-64')}>
      <div className={cn('flex h-14 shrink-0 items-center gap-2.5 border-b border-sidebar-border', narrow ? 'justify-center px-2' : 'px-4')}>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
          <Sparkles className="h-4 w-4" aria-hidden />
        </div>
        {!narrow && (
          <div className="min-w-0 leading-tight">
            <p className="truncate text-[15px] font-semibold tracking-tight">Business Sense</p>
            <p className="truncate text-[11px] text-sidebar-muted">ImpactPlus</p>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin">
        {NAV.map((group) => (
          <div key={group.label} className="mb-4">
            {!narrow ? (
              <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted">{group.label}</p>
            ) : <div className="mx-auto mb-2 h-px w-6 bg-sidebar-border" aria-hidden />}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const link = (
                  <NavLink to={item.path} onClick={onNavigate} data-tour={`nav-${item.key}`}
                    className={({ isActive }) => cn(
                      'group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                      narrow && 'justify-center px-0',
                      isActive ? 'bg-sidebar-accent text-primary' : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
                    )}>
                    {({ isActive }) => (
                      <>
                        <item.icon className={cn('h-[18px] w-[18px] shrink-0', isActive ? 'text-primary' : 'text-sidebar-muted group-hover:text-sidebar-foreground')} aria-hidden />
                        {narrow ? <span className="sr-only">{item.label}</span> : <span className="truncate">{item.label}</span>}
                      </>
                    )}
                  </NavLink>
                );
                return <li key={item.key}>{narrow ? <Tooltip content={item.label} side="right">{link}</Tooltip> : link}</li>;
              })}
            </ul>
          </div>
        ))}
      </nav>

      {!mobile && onToggle && (
        <div className="border-t border-sidebar-border p-3">
          <button type="button" onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn('flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground', narrow && 'justify-center px-0')}>
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
          </button>
        </div>
      )}
    </aside>
  );
}
